import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactElement } from "react";
import { QtyInput, QueryStatus, ScaledInput, useToast } from "@cx/ui";
import type { ProductStructureView } from "@cx/contracts";
import { formatBRL, formatPct, formatQty, toPct4 } from "@cx/domain";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import {
  requiredQ4,
  rowsFromStructure,
  structureCostCents,
  structurePayload,
  structureProblem,
  type StructureRow,
} from "../production/labels";
import { ProductPicker } from "./ProductPicker";

const pctNumber = (pct4: number) => formatPct(pct4).replace("%", "");

/**
 * Aba Estrutura (RF-SUP-05): insumos por 1 unidade do produto, com perda (%), custo estimado pelo custo médio dos
 * insumos. Salva à parte do cadastro (PUT /products/:id/structure); a ordem de produção copia esta lista.
 */
export function StructureTab({ productId, canEdit }: { productId: string; canEdit: boolean }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["product-structure", companyId, productId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<ProductStructureView>(`/products/${productId}/structure`),
  });
  const [rows, setRows] = useState<StructureRow[] | null>(null);
  useEffect(() => {
    if (q.data && rows === null) setRows(rowsFromStructure(q.data.items));
  }, [q.data, rows]);
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: (r: StructureRow[]) =>
      api(`/products/${productId}/structure`, { method: "PUT", body: JSON.stringify(structurePayload(r)) }),
    onSuccess: () => {
      toast("success", "Estrutura salva");
      setRows(null);
      setTried(false);
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const list = rows ?? [];
  const problem = structureProblem(list);
  const cost = structureCostCents(list);
  const set = (i: number, patch: Partial<StructureRow>) =>
    setRows(list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      <QueryStatus loading={q.isLoading} error={q.error} />
      {rows !== null && (
        <>
          {list.length === 0 ? (
            <p className="text-muted">
              Nenhum insumo. Cadastre a estrutura para fabricar este produto por ordem de produção.
            </p>
          ) : (
            <div
              className="table-responsive mb-3"
              tabIndex={0}
              role="region"
              aria-label="Estrutura de produção (rolagem)"
            >
              <table className="table align-middle" aria-label="Estrutura de produção">
                <thead>
                  <tr>
                    <th scope="col">Insumo</th>
                    <th scope="col">Un</th>
                    <th scope="col" className="text-end">
                      Quantidade por unidade
                    </th>
                    <th scope="col" className="text-end">
                      Perda (%)
                    </th>
                    <th scope="col" className="text-end">
                      Necessário
                    </th>
                    {cost !== null && (
                      <th scope="col" className="text-end">
                        Custo médio
                      </th>
                    )}
                    <th scope="col">
                      <span className="visually-hidden">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r, i) => (
                    <tr key={r.componentId}>
                      <td>
                        {r.name}
                        {r.sku && <span className="text-muted small ms-2">{r.sku}</span>}
                      </td>
                      <td>{r.unit}</td>
                      <td style={{ minWidth: 120 }}>
                        <QtyInput
                          aria-label={`Quantidade por unidade de ${r.name}`}
                          className="form-control form-control-sm text-end"
                          decimals={0}
                          disabled={!canEdit}
                          value={r.qtyQ4}
                          onChange={(v) => set(i, { qtyQ4: v })}
                        />
                      </td>
                      <td style={{ minWidth: 100 }}>
                        <ScaledInput
                          aria-label={`Perda de ${r.name}`}
                          className="form-control form-control-sm text-end"
                          disabled={!canEdit}
                          value={r.lossPct4}
                          parse={toPct4}
                          format={pctNumber}
                          onChange={(v) => set(i, { lossPct4: v })}
                        />
                      </td>
                      <td className="text-end qty">
                        {(() => {
                          const n = requiredQ4(r.qtyQ4, r.lossPct4, 10_000);
                          return n === null ? "—" : formatQty(n);
                        })()}
                      </td>
                      {cost !== null && <td className="text-end money">{formatBRL(r.avgCostCents ?? 0)}</td>}
                      <td className="text-end">
                        {canEdit && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            aria-label={`Remover insumo ${r.name}`}
                            onClick={() => setRows(list.filter((_, j) => j !== i))}
                          >
                            Remover
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {cost !== null && (
                  <tfoot>
                    <tr>
                      <th scope="row" colSpan={5} className="text-end">
                        Custo dos insumos por unidade
                      </th>
                      <td className="text-end money fw-semibold" data-testid="structure-cost">
                        {formatBRL(cost)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
          {canEdit && (
            <>
              <ProductPicker
                label="Adicionar insumo"
                inputId="structure-search"
                target="à estrutura"
                exclude={[...list.map((r) => r.componentId), productId]}
                onPick={(p) =>
                  setRows([
                    ...list,
                    {
                      componentId: p.id,
                      name: p.name,
                      sku: p.sku,
                      unit: p.unit,
                      qtyQ4: 10_000,
                      lossPct4: 0,
                      ...(p.costCents !== undefined ? { avgCostCents: p.costCents } : {}),
                    },
                  ])
                }
              />
              {tried && problem && (
                <p className="text-danger small" role="alert">
                  {problem}
                </p>
              )}
              <button
                type="button"
                className="btn btn-primary"
                disabled={save.isPending}
                onClick={() => {
                  setTried(true);
                  if (!problem) save.mutate(list);
                }}
              >
                Salvar estrutura
              </button>
            </>
          )}
          <p className="text-muted small mt-3 mb-0">
            Necessário = quantidade por unidade × (1 + perda). A ordem de produção baixa os insumos e dá
            entrada no produto com o custo apurado. O custo médio é o atual; o custo real sai de cada
            apontamento.
          </p>
        </>
      )}
    </div>
  );
}
